import { useEffect, useRef } from 'react';

/**
 * Hook to execute a callback when GrowthBook becomes ready
 * Works regardless of whether GrowthBook is enabled or not
 *
 * @param callback - Function to call when GrowthBook is ready
 * @param deps - Optional dependency array for the callback
 *
 * @example
 * ```tsx
 * useOnGrowthBookReady(() => {
 *   console.log('GrowthBook is ready!');
 * });
 * ```
 */
export function useOnGrowthBookReady(callback: () => void, deps?: React.DependencyList): void {
  const hasRun = useRef(false);

  useEffect(() => {
    const handleGrowthBookReady = () => {
      if (hasRun.current) return;
      hasRun.current = true;
      callback();
    };

    // Listen for the custom event
    window.addEventListener('growthbook:ready', handleGrowthBookReady);

    // Fallback: if GrowthBook is already ready when this mounts
    const timeoutId = setTimeout(() => {
      if (!hasRun.current) {
        callback();
      }
    }, 100);

    return () => {
      window.removeEventListener('growthbook:ready', handleGrowthBookReady);
      clearTimeout(timeoutId);
    };
  }, deps);

  // Reset hasRun when deps change (allows callback to run again)
  useEffect(() => {
    hasRun.current = false;
  }, deps);
}
